import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { AI_CAPABILITIES, AI_FLOW } from "../data/site";

export default function AIAutomationSection({ showCta = true }) {
  return (
    <section className="bg-white py-24 md:py-32" data-testid="ai-automation-section">
      <div className="container-x">
        <SectionHeading
          eyebrow="AI & Automation"
          title="AI that works with your business."
          subtitle="We build practical AI solutions that solve real problems — not AI for the sake of AI."
          max="max-w-3xl"
        />

        {/* Workflow */}
        <div className="mt-16 rounded-3xl border border-slate-200 bg-[#F8FAFC] p-6 md:p-10" data-testid="ai-workflow">
          <div className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center">
            {AI_FLOW.map((node, i) => (
              <div key={node} className="flex items-center gap-3 lg:flex-1">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className={`flex-1 rounded-2xl border px-4 py-4 text-center text-sm font-semibold ${
                    i === 1 ? "accent-bg border-transparent text-[#07111F]" :
                    i === AI_FLOW.length - 1 ? "border-[#07111F] bg-[#07111F] text-white" :
                    "border-slate-200 bg-white text-[#07111F]"
                  }`}
                >
                  {node}
                </motion.div>
                {i < AI_FLOW.length - 1 && (
                  <ChevronRight className="hidden shrink-0 text-slate-300 lg:block" size={18} />
                )}
              </div>
            ))}
          </div>
          <p className="mt-6 text-center font-mono text-xs text-slate-400">
            Grounded in your knowledge · connected to your tools · accountable results
          </p>
        </div>

        {/* Capabilities */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {AI_CAPABILITIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.label + i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition-colors duration-400 hover:border-[#07111F]"
                data-testid={`ai-capability-${i}`}
              >
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#07111F] text-cyan-300 transition-colors duration-400 group-hover:accent-bg group-hover:text-[#07111F]">
                  <Icon size={17} strokeWidth={1.8} />
                </span>
                <span className="text-sm font-medium text-[#07111F]">{c.label}</span>
              </motion.div>
            );
          })}
        </div>

        {showCta && (
          <div className="mt-12">
            <Link to="/contact" className="btn-primary" data-testid="build-with-ai-cta">
              Build With AI <ArrowUpRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
